import { IHttpApi } from '@cian/http-api/shared';
import { ILogger } from '@cian/logger/shared';
import { GenericError } from '@cian/peperrors/shared';
import { getLogger } from '@cian/logger/browser';
import { getHttpApi } from '@cian/http-api/browser';

import {
  sendTelemetryTimer as telemetrySendTelemetryTimer,
  sendTelemetryCounter as telemetrySendTelemetryCounter,
} from '../../services/telemetry';

/**
 * Хелпер-класс для логирования по яндекс-саджесту в браузере
 * Умеет отправлять телеметрию (с инициализацией начала отсчета времени)
 * Умеет логировать, при этом использует либо error либо warning в зав-ти от поля isStrict
 * */
export interface IYandexMonitorConstructorParams {
  isStrict: boolean;
  mcsName: string;
}

export class YandexMonitor {
  public isStrict: boolean;
  public mcsName: string;
  private readonly httpApi: IHttpApi;
  private readonly logger: ILogger;
  private readonly trackTelemetryTimer: () => void;

  public constructor(config: IYandexMonitorConstructorParams) {
    this.isStrict = config.isStrict;
    this.httpApi = getHttpApi();
    this.logger = getLogger();
    this.mcsName = config.mcsName;

    this.trackTelemetryTimer = this.prepareTelemetryTimerTracker();
  }

  private get loggingMethod() {
    /** Если не забайндить, контекст теряется и логгер не работает.  */
    return this.isStrict ? this.logger.error.bind(this.logger) : this.logger.warning.bind(this.logger);
  }

  private prepareTelemetryTimerTracker() {
    const startTimeRequest = new Date().getTime();

    return () => {
      const endTimeRequest = new Date().getTime() - startTimeRequest;

      telemetrySendTelemetryTimer({
        httpApi: this.httpApi,
        key: `${this.mcsName}.yandex-suggest.timer`,
        value: endTimeRequest,
      });
    };
  }

  public sendTelemetryCounter() {
    telemetrySendTelemetryCounter({
      httpApi: this.httpApi,
      key: `${this.mcsName}.yandex-suggest.200`,
    });
  }

  public sendTelemetryTimer() {
    this.trackTelemetryTimer();
  }

  public log(e: Error | GenericError) {
    this.loggingMethod('Invalid yandex suggest api request', {
      errorMessage: e.message,
    });
  }
}

/**
 * Эту ошибку следует бросать, когда происходит ошибка внутри яндекс-скрипта
 * Например, такую ошибку можно отловить, если отключить интернет или заблокировать url яндекс саджеста
 * */
export class YandexScriptError extends GenericError {
  public constructor() {
    const props = {
      message: 'Yandex suggest script error',
      type: YandexScriptError.name,
      domain: '',
    };

    /* istanbul ignore next */ super(props);
    Object.setPrototypeOf(this, YandexScriptError.prototype);
  }
}
